import React, { useMemo, useState } from 'react';
import { LockIcon, SearchIcon, UserSearchIcon } from 'lucide-react';
import { useData } from '../../store/DataContext';
import { ConsultationTypeBadge } from '../../components/ui/ConsultationTypeBadge';
import { ConsultationType } from '../../types';
import { PatientRegistrationDetails } from './PatientRegistrationDetails';
type SearchRow = {
  id: string;
  patientNumber: string;
  fullName: string;
  sex: string;
  contact: string;
  lastVisit: Date | null;
  consultationType?: ConsultationType;
};
const MAX_RESULTS = 50;
const digitsOnly = (value: string) => value.replace(/\D/g, '');

/**
 * Reception PATIENT SEARCH — strictly view-only. Shows basic identification
 * and the most recent visit's consultation type; no clinical data, no actions.
 */
export function PatientSearch() {
  const {
    patients,
    encounters
  } = useData();
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Most recent encounter per patient (by visit start time).
  const lastVisitByPatient = useMemo(() => {
    const map = new Map<string, {
      at: Date;
      type?: ConsultationType;
    }>();
    encounters.forEach(e => {
      const at = new Date(e.startedAt);
      if (Number.isNaN(at.getTime())) return;
      const current = map.get(e.patientId);
      if (!current || at > current.at) {
        map.set(e.patientId, {
          at,
          type: e.consultationType
        });
      }
    });
    return map;
  }, [encounters]);
  const results = useMemo<SearchRow[]>(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const qDigits = digitsOnly(q);
    return patients.filter(p => {
      const fullName = `${p.firstName} ${p.lastName}`.toLowerCase();
      return p.bmcFileNumber.toLowerCase().includes(q) || fullName.includes(q) || `${p.lastName} ${p.firstName}`.toLowerCase().includes(q) || qDigits.length >= 3 && digitsOnly(p.contact || '').includes(qDigits);
    }).map(p => {
      const last = lastVisitByPatient.get(p.id);
      return {
        id: p.id,
        patientNumber: p.bmcFileNumber,
        fullName: `${p.firstName} ${p.lastName}`,
        sex: p.sex,
        contact: p.contact || '—',
        lastVisit: last?.at ?? null,
        consultationType: last?.type
      };
    }).sort((a, b) => a.fullName.localeCompare(b.fullName));
  }, [patients, lastVisitByPatient, query]);
  const shown = results.slice(0, MAX_RESULTS);
  // Looked up live by unique id so the panel always shows the saved record.
  const selectedPatient = selectedId ? patients.find(p => p.id === selectedId) : undefined;
  return <section aria-labelledby="patient-search-heading" className="rounded-xl bg-white p-4 shadow-sm sm:p-6" data-id="element-10154" data-mp="9bc5iy2c67ai">
      <header className="mb-4 flex flex-wrap items-start justify-between gap-3" data-id="element-10155" data-mp="x05472acet8t">
        <div data-id="element-10156" data-mp="11ser09g5vd6v">
          <h2 id="patient-search-heading" className="text-lg font-bold text-gray-900" data-id="element-10157" data-mp="11sy8jp1wfzkd">
            Patient Search
          </h2>
          <p className="text-sm text-gray-500" data-id="element-10158" data-mp="1753a2ajw5sc3">
            Look up a patient and click their name to see their registration
            details.
          </p>
        </div>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600" data-id="element-10159" data-mp="1kvqduh4ie8f1">
          <LockIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-10160" data-mp="s7koti6f9x9e" />
          View only
        </span>
      </header>

      <div className="relative" data-id="element-10161" data-mp="o6qzqlh8ucex">
        <label htmlFor="patient-search-input" className="sr-only" data-id="element-10162" data-mp="o7tqmtekgeuu">
          Search by patient number, full name or phone number
        </label>
        <SearchIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" aria-hidden="true" data-id="element-10163" data-mp="x6xba1tdt2bg" />
        <input id="patient-search-input" type="search" autoFocus autoComplete="off" value={query} onChange={e => setQuery(e.target.value)} placeholder="Patient number, full name or phone number" className="block w-full rounded-lg border border-gray-300 py-3 pl-10 pr-3 text-sm focus:border-chrome-active focus:outline-none focus:ring-1 focus:ring-chrome-active" data-id="element-10164" data-mp="2djcd5d6peqw3" />
      </div>

      <div className="mt-5" aria-live="polite" data-id="element-10165" data-mp="w503nfz6867m">
        {!query.trim() ? <div className="py-12 text-center text-gray-500" data-id="element-10166" data-mp="1144c24e9tzww">
            <UserSearchIcon className="mx-auto mb-2 h-10 w-10 text-gray-300" data-id="element-10167" data-mp="113kyezvnlxoo" />
            <p className="text-sm" data-id="element-10168" data-mp="161mnq2joat8l">
              Start typing to find a patient.
            </p>
          </div> : shown.length === 0 ? <p className="py-12 text-center text-sm text-gray-500" data-id="element-10169" data-mp="1fsr1x01iya2d">
            No patients match "{query.trim()}".
          </p> : <>
            <p className="mb-2 text-xs text-gray-500" data-id="element-10170" data-mp="1jvx8y9wgo59w">
              {results.length} match{results.length === 1 ? '' : 'es'}
              {results.length > MAX_RESULTS ? ` · showing first ${MAX_RESULTS}, refine your search` : ''}
            </p>
            <div className="overflow-x-auto rounded-lg border border-gray-200" data-id="element-10171" data-mp="34bbqa32zmmhv">
              <table className="min-w-full divide-y divide-gray-200 text-sm" data-id="element-10172" data-mp="30a9tdswjmmj2">
                <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-600" data-id="element-10173" data-mp="2vbosrsddq0u3">
                  <tr data-id="element-10174" data-mp="3s2haeufyo0xz">
                    <th scope="col" className="px-4 py-3" data-id="element-10175" data-mp="3s0nzv4288wkp">Patient No.</th>
                    <th scope="col" className="px-4 py-3" data-id="element-10176" data-mp="3nzudwg8chrug">Full Names</th>
                    <th scope="col" className="px-4 py-3" data-id="element-10177" data-mp="3j9nq2ilnqbd9">Sex</th>
                    <th scope="col" className="px-4 py-3" data-id="element-10178" data-mp="3j82qufoee492">Contact</th>
                    <th scope="col" className="px-4 py-3" data-id="element-10179" data-mp="3e9hsymwmbwns">Last Visit</th>
                    <th scope="col" className="px-4 py-3" data-id="element-10180" data-mp="fkamznv5clik">Consultation Type</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white" data-id="element-10181" data-mp="asswjtqr23bu">
                  {shown.map(row => <tr key={row.id} onClick={() => setSelectedId(row.id)} className="cursor-pointer hover:bg-blue-50/60" data-id="element-10182" data-mp="trdv2j97lpk3">
                      <td className="whitespace-nowrap px-4 py-3 font-mono text-blue-700" data-id="element-10183" data-mp="kx98wd50t2aw">
                        {row.patientNumber}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3" data-id="element-10184" data-mp="az1tvzuakubj">
                        <button type="button" onClick={event => {
                    event.stopPropagation();
                    setSelectedId(row.id);
                  }} className="font-medium text-gray-900 underline-offset-2 hover:text-blue-700 hover:underline focus:outline-none focus-visible:rounded focus-visible:ring-2 focus-visible:ring-chrome-active" data-id="element-10185" data-mp="b1xrto653izz">
                          {row.fullName}
                        </button>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-gray-700" data-id="element-10186" data-mp="3ku7ied9urg2q">
                        {row.sex}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-gray-700" data-id="element-10187" data-mp="3ummzz9h8m2n3">
                        {row.contact}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-gray-700" data-id="element-10188" data-mp="3ptk42uozmh3q">
                        {row.lastVisit ? row.lastVisit.toLocaleDateString('en-GB') : <span className="text-xs font-semibold uppercase text-gray-500" data-id="element-10189" data-mp="hfuyviuvx6wz">
                            No previous visit
                          </span>}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3" data-id="element-10190" data-mp="2j6ookyyl74ip">
                        {!row.lastVisit ? <span className="text-gray-400" data-id="element-10191" data-mp="2e96nfuqt9tl9">—</span> : row.consultationType ? <ConsultationTypeBadge type={row.consultationType} size="sm" data-id="element-10192" data-mp="1tbxpcxjy0o6f" /> : <span className="text-xs italic text-gray-500" data-id="element-10193" data-mp="225apokkc6s2j">
                            Not recorded
                          </span>}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </>}
      </div>
      {selectedPatient && <PatientRegistrationDetails patient={selectedPatient} onClose={() => setSelectedId(null)} data-id="element-10194" data-mp="1je01wc9wqvev" />}
    </section>;
}